// SNIGJ - Admin Security Configuration Page (Account Lockout)
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Shield, Save, Unlock, Lock, Users, Clock, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const AdminSecurityConfigPage = () => {
  const { language } = useAuth();
  const [config, setConfig] = useState({
    max_failed_attempts: 5,
    lockout_duration_minutes: 30,
    notify_admin_on_lock: true,
    notify_user_by_email: true
  });
  const [lockedAccounts, setLockedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [configRes, lockedRes] = await Promise.all([
        api.get('/admin/security-config'),
        api.get('/admin/locked-accounts')
      ]);
      setConfig(configRes.data);
      setLockedAccounts(lockedRes.data.locked_accounts || []);
    } catch (err) {
      console.error('Failed to load security config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/security-config', {
        max_failed_attempts: parseInt(config.max_failed_attempts) || 5,
        lockout_duration_minutes: parseInt(config.lockout_duration_minutes) || 30,
        notify_admin_on_lock: config.notify_admin_on_lock,
        notify_user_by_email: config.notify_user_by_email
      });
      toast.success(language === 'fr' ? 'Configuration sauvegardée' : 'Configuration saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlock = async (userId, name) => {
    try {
      await api.post(`/admin/unlock-account/${userId}`);
      toast.success(language === 'fr' ? `Compte de ${name} déverrouillé` : `${name}'s account unlocked`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-4">
          <div className="h-8 w-64 bg-muted animate-pulse rounded" />
          <div className="h-64 bg-muted animate-pulse rounded" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl" data-testid="security-config-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary" />
            {language === 'fr' ? 'Configuration de Sécurité' : 'Security Configuration'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {language === 'fr' ? 'Gérer le verrouillage automatique des comptes et les paramètres de sécurité' : 'Manage automatic account lockout and security settings'}
          </p>
        </div>

        {/* Lockout Config */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Lock className="w-5 h-5" />
              {language === 'fr' ? 'Verrouillage Automatique' : 'Automatic Lockout'}
            </CardTitle>
            <CardDescription>
              {language === 'fr' 
                ? 'Le compte sera verrouillé après le nombre de tentatives échouées configuré ci-dessous'
                : 'Account will be locked after the configured number of failed attempts'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="max-attempts" className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  {language === 'fr' ? 'Tentatives max avant verrouillage' : 'Max attempts before lock'}
                </Label>
                <Input
                  id="max-attempts"
                  type="number"
                  min={2}
                  max={20}
                  value={config.max_failed_attempts}
                  onChange={(e) => setConfig({ ...config, max_failed_attempts: e.target.value })}
                  data-testid="max-attempts-input"
                />
                <p className="text-xs text-muted-foreground">
                  {language === 'fr' ? 'Entre 2 et 20 tentatives' : 'Between 2 and 20 attempts'}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="lockout-duration" className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-600" />
                  {language === 'fr' ? 'Durée du verrouillage (minutes)' : 'Lockout duration (minutes)'}
                </Label>
                <Input
                  id="lockout-duration"
                  type="number"
                  min={5}
                  max={1440}
                  value={config.lockout_duration_minutes}
                  onChange={(e) => setConfig({ ...config, lockout_duration_minutes: e.target.value })}
                  data-testid="lockout-duration-input"
                />
                <p className="text-xs text-muted-foreground">
                  {language === 'fr' ? 'Entre 5 min et 24h (1440 min)' : 'Between 5 min and 24h (1440 min)'}
                </p>
              </div>
            </div>

            <div className="space-y-3 pt-2">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="notify-admin"
                  checked={config.notify_admin_on_lock}
                  onChange={(e) => setConfig({ ...config, notify_admin_on_lock: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="notify-admin" className="cursor-pointer text-sm">
                  {language === 'fr' ? 'Notifier les administrateurs quand un compte est verrouillé' : 'Notify admins when account is locked'}
                </Label>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="notify-email"
                  checked={config.notify_user_by_email}
                  onChange={(e) => setConfig({ ...config, notify_user_by_email: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="notify-email" className="cursor-pointer text-sm">
                  {language === 'fr' ? 'Envoyer un email à l\'utilisateur quand son compte est verrouillé' : 'Email user when account is locked'}
                </Label>
              </div>
            </div>

            <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-security-config-btn">
              <Save className="w-4 h-4" />
              {saving ? (language === 'fr' ? 'Sauvegarde...' : 'Saving...') : (language === 'fr' ? 'Sauvegarder' : 'Save')}
            </Button>
          </CardContent>
        </Card>

        {/* Locked Accounts */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5" />
                {language === 'fr' ? 'Comptes Verrouillés' : 'Locked Accounts'}
                {lockedAccounts.length > 0 && <Badge className="bg-red-500 text-white">{lockedAccounts.length}</Badge>}
              </CardTitle>
              <CardDescription>
                {language === 'fr' ? 'Comptes actuellement verrouillés suite à des tentatives échouées' : 'Currently locked accounts due to failed attempts'}
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={loadData} className="gap-1">
              <RefreshCw className="w-3 h-3" />
              {language === 'fr' ? 'Actualiser' : 'Refresh'}
            </Button>
          </CardHeader>
          <CardContent>
            {lockedAccounts.length > 0 ? (
              <div className="space-y-3">
                {lockedAccounts.map((acc) => (
                  <div key={acc.user_id} className="flex items-center justify-between p-3 rounded border border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-950/20" data-testid={`locked-account-${acc.user_id}`}>
                    <div className="min-w-0">
                      <p className="font-medium">{acc.name || acc.email}</p>
                      <p className="text-xs text-muted-foreground">{acc.email} | {acc.role?.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-red-600 mt-0.5">
                        {acc.failed_login_attempts} {language === 'fr' ? 'tentatives' : 'attempts'} | 
                        {language === 'fr' ? ' Verrouillé jusqu\'au' : ' Locked until'}: {new Date(acc.locked_until).toLocaleString('fr-FR')}
                      </p>
                    </div>
                    <Button size="sm" onClick={() => handleUnlock(acc.user_id, acc.name || acc.email)} className="gap-1 shrink-0" data-testid={`unlock-btn-${acc.user_id}`}>
                      <Unlock className="w-3 h-3" />
                      {language === 'fr' ? 'Déverrouiller' : 'Unlock'}
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-sm text-muted-foreground">
                <Lock className="w-8 h-8 mx-auto mb-2 opacity-30" />
                {language === 'fr' ? 'Aucun compte verrouillé' : 'No locked accounts'}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminSecurityConfigPage;
